import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { authService } from "../services/authService";
import { useAuth } from "../context/AuthContext";
import { User } from "../types";
import AuthShell from "../components/auth/AuthShell";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Sayfa yüklendiğinde kaydedilmiş email'i kontrol et
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Location state'inden veya sessionStorage'dan redirect URL'ini al
  const fromLocation = (location.state as any)?.from;
  const fromPath = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search || ""}`
    : sessionStorage.getItem("redirectAfterLogin") || "/orders";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await authService.login({ email, password });

      // Backend'den gelen response formatına göre user objesi oluştur
      let user: User;
      if (response.user) {
        user = response.user;
      } else {
        user = {
          userId: response.userId || response.email,
          email: response.email || email,
          firstName: response.firstName || "User",
          lastName: response.lastName || "",
          role: response.role || "User",
          workshops: response.workshops || [],
          isActive: true,
        };
      }

      // Beni hatırla işlemi
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      // Her durumda login fonksiyonunu çağır (AuthContext'i güncelle)
      login(response.token, user);

      // İlk giriş kontrolü
      if (response.isFirstLogin === true) {
        setLoading(false);
        navigate("/change-password", {
          state: { isFirstLogin: true },
          replace: true,
        });
        return;
      }

      sessionStorage.removeItem("redirectAfterLogin");
      navigate(fromPath, { replace: true });
    } catch (err: any) {
      let errorMessage = "Giriş başarısız. Lütfen bilgilerinizi kontrol edin.";

      if (
        err.code === "NETWORK_ERROR" ||
        err.message?.includes("ERR_EMPTY_RESPONSE")
      ) {
        errorMessage =
          "Sunucuya bağlanılamıyor. Lütfen sunucunun çalıştığından emin olun.";
      } else if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === "object" && data !== null) {
          if (data.errors) {
            errorMessage = Object.entries(data.errors)
              .map(([, messages]: [string, any]) =>
                Array.isArray(messages) ? messages.join(", ") : messages,
              )
              .join("\n");
          } else if (data.title) {
            errorMessage = data.title;
          } else if (data.message) {
            errorMessage = data.message;
          } else if (typeof data === "string") {
            errorMessage = data;
          }
        } else if (typeof data === "string") {
          errorMessage = data;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Giriş Yap" subtitle="Kurumsal hesabınızla oturum açın">
      <form onSubmit={handleSubmit} className="lg-form" noValidate>
            {error && (
              <div className="lg-error" role="alert">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <div className="lg-field">
              <label htmlFor="email">E-posta adresi</label>
              <input
                id="email"
                type="email"
                placeholder="ad.soyad@sirketiniz.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="lg-field">
              <label htmlFor="password">Şifre</label>
              <div className="lg-pass-wrap">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lg-pass-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Şifreyi göster"
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C5 20 1 12 1 12a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Beni hatırla + Şifremi unuttum aynı satırda: kart tek düğmeyle biter */}
            <div className="lg-row">
              <label className="lg-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Beni hatırla</span>
              </label>
              <Link className="lg-forgot" to="/forgot-password">Şifremi unuttum</Link>
            </div>

            <button type="submit" className="lg-submit" disabled={loading} aria-busy={loading || undefined}>
              {loading ? (
                <>
                  <span className="lg-spinner" aria-hidden="true"></span>
                  Giriş yapılıyor…
                </>
              ) : (
                "Giriş Yap"
              )}
            </button>
      </form>
    </AuthShell>
  );
};

export default Login;
